import createCache from "@emotion/cache";
import { CacheProvider } from "@emotion/react";
import { createStyles, makeStyles, Theme } from "@material-ui/core";
import { CssBaseline } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import React from "react";
import { Control, Controller, RegisterOptions } from "react-hook-form";
import rtlPlugin from "stylis-plugin-rtl";
const theme = createTheme({
  direction: "rtl",
});
const cacheRtl = createCache({
  key: "muirtl",
  stylisPlugins: [rtlPlugin],
});
interface IProps {
  name: string;
  options: any[];
  disable?: boolean;
  readOnly?: boolean;
  isHeaderTable?: boolean;
  english?: boolean;
  label: any;
  placeholder?: any;
  color?: string;
  control: Control<any>;
  error?: boolean;
  helpText?: string;
  classNames?: any;
  withoutShrink?: any;
  rules?: Exclude<
    RegisterOptions,
    "valueAsNumber" | "valueAsDate" | "setValueAs"
  >;
  defaultValue?: any;
  selectChanged?: (value: any) => void;
  changeHandler?: (value: any) => void;
}

const SelectInputAutoComplete: React.FC<IProps> = ({
  options,
  label,
  placeholder,
  color,
  disable,
  english,
  readOnly,
  control,
  name,
  error,
  helpText,
  classNames,
  rules,
  defaultValue,
  selectChanged,
  changeHandler,
  withoutShrink,
}) => {
  const Styles = makeStyles((theme: Theme) =>
    createStyles({
      selectInput: {
        "& .MuiFilledInput-root.MuiFilledInput-underline.MuiInputBase-root.MuiInputBase-colorPrimary":
          {
            backgroundColor: "var(--background-color, #F9F9F9)",
            borderBottomLeftRadius: "5px",
            borderBottomRightRadius: "5px",
            "&, &:before": {
              borderBottom: "1px solid var(--gray-gray-100, #E7EAEC)",
            },
            "&, &:after": {
              borderBottom: "none !important",
            },
          },
        "& .MuiFilledInput-root.MuiFilledInput-underline.MuiInputBase-root.MuiInputBase-colorPrimary.Mui-error":
          {
            "&, &:before": {
              borderBottom: "1px solid var(--gray-gray-100, #E7EAEC)",
            },
            "&, &:after": {
              borderBottom: "1px solid red !important",
            },
          },
        "& .MuiInputLabel-root": {
          fontSize: "12px !important",
          color: "var(--gray-gray-500, #7B7B7B)",
        },
        "& input": {
          padding: "0 !important",
          marginTop: "-20px",
          fontFamily: english ? "Regular !important" : "",
        },
        "& .MuiFilledInput-underline.MuiInputBase-root.MuiInputBase-colorPrimary":
          {
            "&, &:before": {
              borderBottom:
                "1px solid var(--gray-gray-100, #E7EAEC) !important",
              borderRadius: "5px",
            },
          },
        "& .MuiFilledInput-root": {
          borderRadius: "10px",
          border: "1px solid var(--gray-gray-100, #E7EAEC)",
          background: "var(--background-color, #F9F9F9)",
        },
        borderRadius: "5px",
        "& .MuiInputBase-root": {
          borderRadius: "5px",
          border: "1px solid var(--gray-gray-100, #E7EAEC)",
          background: "var(--background-color, #F9F9F9)",
          // backgroundColor: "white !important",
          minHeight: "48px",
          Height: "48px",
          color: theme.customColors.title,
        },
        "& .MuiOutlinedInput-root": {
          "&.Mui-focused fieldset": {
            borderColor: theme.palette.text.primary + "!important",
          },
        },
        "& label.Mui-focused": {
          color: theme.palette.text.primary,
          // top: '5px ',
        },
        "& .MuiFormLabel-colorPrimary": {
          color: theme.customColors.title,
          // top: '5px ',
        },
        "& .MuiOutlinedInput-notchedOutline": {
          // borderColor: theme.customColors.title + " !important",
          // borderTop: "none",
          // borderLeft: "none",
          // borderRight: "none",
          // borderRadius: "0",
        },
        "& .MuiSvgIcon-root": {
          color: "#474747",
        },
        "& .Mui-disabled.MuiInputBase-inputSizeSmall": {
          WebkitTextFillColor: theme.customColors.disableTitle + " !important",
        },
        "& .MuiFormLabel-root.MuiFormLabel-colorPrimary.Mui-disabled": {
          WebkitTextFillColor: theme.customColors.disableTitle + " !important",
        },
      },
      label: {
        color: "var(--gray-gray-800, #2C2C2C)",
        textAlign: "left",
        fontSize: "14px",
        fontStyle: "normal",
        fontWeight: 500,
        lineHeight: "24px",
      },
    })
  );
  const [val, setVal] = React.useState(null);
  const classes = Styles();
  return (
    <Controller
      render={({ field }) => {
        if (val !== field.value && field.value !== undefined) {
          setVal(field.value);
        }
        return (
          <>
            <div className="row w-100 m-0 p-0">
              <div className={classes.label}>{label}</div>
            </div>
            <CacheProvider value={cacheRtl}>
              <ThemeProvider theme={theme}>
                <CssBaseline />
                <Autocomplete
                  id="country-select-demo"
                  value={val}
                  size="medium"
                  className="p-0"
                  options={options}
                  autoHighlight
                  disabled={disable}
                  getOptionLabel={(option) =>
                    option.color ??
                    option.text ??
                    option.name ??
                    option.description ??
                    " "
                  }
                  onChange={(_, value) => {
                    if (!!value) {
                      field.onChange(value);
                    } else {
                      field.onChange();
                    }
                    if (!!changeHandler) {
                      changeHandler(value);
                    }
                    setVal(value);
                    if (!!selectChanged) {
                      selectChanged(value);
                    }
                  }}
                  renderOption={(props, option) => (
                    <Box
                      component="li"
                      sx={{
                        fontSize: 12,
                        fontFamily: english ? "Regular !important" : "",
                        "& > img": { mr: 2, flexShrink: 0 },
                        direction: "ltr",
                      }}
                      {...props}
                    >
                      <img loading="lazy" width="20" />
                      {option.color ??
                        option.text ??
                        option.name ??
                        option.description ??
                        " "}
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      InputLabelProps={{ shrink: false }}
                      value={val}
                      placeholder={placeholder}
                      variant="filled"
                      inputProps={{
                        ...params.inputProps,
                        readOnly: readOnly,
                      }}
                      onChange={() => {
                        field.onChange();
                      }}
                      error={error}
                      helperText={helpText}
                      className={[classes.selectInput, classNames].join(" ")}
                    />
                  )}
                />
              </ThemeProvider>
            </CacheProvider>
          </>
        );
      }}
      name={name}
      control={control}
      rules={rules}
      defaultValue={defaultValue}
    />
  );
};

export default SelectInputAutoComplete;
